import { useState } from 'react';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '@/components/ui/navigation-menu';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Header = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/75 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-testid="site-header">
      <div className="mx-auto w-full max-w-[var(--container)] px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex h-14 sm:h-16 items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-2">
            <span className="text-lg sm:text-xl md:text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Homely Solutions</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Services</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4">
                      <li>
                        <NavigationMenuLink asChild>
                          <a href="/nettoyage" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">Nettoyage</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Canapé, terrasse, vitres, fin de chantier</p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <a href="/conciergerie" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">Conciergerie Airbnb</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Gestion complète de votre location saisonnière</p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <a href="/renovation" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">Rénovation</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Cuisine, salle de bain, peinture, parquet</p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <a href="/montage" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">Montage & Handy Man</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Meubles, cuisine, petites réparations</p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <a href="/marketing" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">Marketing</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">SEO, publicités, automatisation IA</p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <a href="/immo" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">Vente Immobilière</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">Estimation, mise en valeur, accompagnement</p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <a href="/#about" className="text-sm font-medium transition-colors hover:text-primary">À propos</a>
            <a href="/contact" className="text-sm font-medium transition-colors hover:text-primary">Contact</a>
          </nav>

          {/* CTA Button Desktop */}
          <Button 
            className="hidden md:inline-flex btn-premium rounded-full" 
            onClick={() => navigate('/contact')}
            data-testid="header-primary-cta"
          >
            Obtenir un devis
          </Button>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="header-mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <nav className="flex flex-col space-y-4 mt-8">
                <a href="/" className="text-lg font-medium" onClick={() => setMobileOpen(false)}>Accueil</a>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-muted-foreground">Services</p>
                  <a href="/nettoyage" className="block pl-4 text-base" onClick={() => setMobileOpen(false)}>Nettoyage</a>
                  <a href="/conciergerie" className="block pl-4 text-base" onClick={() => setMobileOpen(false)}>Conciergerie</a>
                  <a href="/renovation" className="block pl-4 text-base" onClick={() => setMobileOpen(false)}>Rénovation</a>
                  <a href="/montage" className="block pl-4 text-base" onClick={() => setMobileOpen(false)}>Montage & Handy Man</a>
                  <a href="/marketing" className="block pl-4 text-base" onClick={() => setMobileOpen(false)}>Marketing</a>
                  <a href="/immo" className="block pl-4 text-base" onClick={() => setMobileOpen(false)}>Immobilier</a>
                </div>
                <a href="/contact" className="text-lg font-medium" onClick={() => setMobileOpen(false)}>Contact</a>
                <Button 
                  className="w-full mt-4" 
                  style={{ backgroundColor: 'var(--cta-blue)' }}
                  onClick={() => { navigate('/contact'); setMobileOpen(false); }}
                >
                  Obtenir un devis
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};